import { useEffect, useRef, VideoHTMLAttributes } from "react";

import white from "@/assets/white.png";
import { useMediaCache } from "@/hooks/use-media-cache";
import { useLockedMediaSrc } from "@/hooks/use-locked-media-src";
import { onNextUserGesture } from "@/utils/user-gesture";

interface VideoPlayerProps {
  src?: string;
  playing?: boolean;
  startTime?: number;
}

export const VideoPlayer = ({
  src,
  playing = false,
  startTime,
  ...props
}: VideoHTMLAttributes<HTMLVideoElement> & VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const blobUrl = useMediaCache(src || null, "video");

  // Whether playback of the current clip has been requested yet. Upgrading the
  // source to the cached blob is only safe before that - afterwards a src swap
  // reloads the element and drops playback on the floor.
  const hasStartedRef = useRef(false);
  const clipKeyRef = useRef(src);
  if (clipKeyRef.current !== src) {
    clipKeyRef.current = src;
    hasStartedRef.current = false;
  }

  const mediaSrc = useLockedMediaSrc(src, blobUrl, !hasStartedRef.current);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleStarted = () => {
      hasStartedRef.current = true;
    };

    // "play" fires as soon as playback is requested, "playing" once frames
    // actually run. Listening to both closes the buffering window between
    // them, which is long on iPad Safari (no data loads until play()).
    video.addEventListener("play", handleStarted);
    video.addEventListener("playing", handleStarted);

    return () => {
      video.removeEventListener("play", handleStarted);
      video.removeEventListener("playing", handleStarted);
    };
  }, []);

  // Start playback as soon as it's requested and let the browser buffer.
  //
  // Do NOT wait for loadeddata/canplaythrough here: iPad Safari doesn't load
  // any video data until playback is initiated, so waiting for "loaded" before
  // calling play() deadlocks into a permanent white poster. If autoplay is
  // blocked (iOS requires user activation for sound), retry inside the next
  // tap, which carries the activation.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !mediaSrc) return;

    if (!playing) {
      video.pause();
      return;
    }

    let cancelGestureRetry: (() => void) | undefined;

    const attemptPlay = () => {
      video.play().catch((error: DOMException) => {
        if (error.name === "NotAllowedError") {
          cancelGestureRetry = onNextUserGesture(attemptPlay);
        } else if (error.name !== "AbortError") {
          console.error("Error playing video:", error);
        }
      });
    };

    attemptPlay();

    return () => cancelGestureRetry?.();
  }, [playing, mediaSrc]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !mediaSrc) return;

    const handleLoadedMetadata = () => {
      if (startTime !== undefined && startTime > 0) {
        video.currentTime = startTime;
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [mediaSrc, startTime]);

  // Ensure video stays muted if muted prop is provided or if video is hidden
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Force mute if muted prop is provided or if video has invisible class (hidden video)
    const isHidden = props.className?.includes("invisible");
    if (props.muted !== undefined || isHidden) {
      video.muted = true;
      // Also set volume to 0 as an additional safeguard
      video.volume = 0;
    }

    // Continuously ensure muted state is maintained
    const ensureMuted = () => {
      if (props.muted !== undefined || isHidden) {
        video.muted = true;
        video.volume = 0;
      }
    };

    // Check on various events to ensure muted state
    video.addEventListener("play", ensureMuted);
    video.addEventListener("loadedmetadata", ensureMuted);
    video.addEventListener("volumechange", ensureMuted);

    return () => {
      video.removeEventListener("play", ensureMuted);
      video.removeEventListener("loadedmetadata", ensureMuted);
      video.removeEventListener("volumechange", ensureMuted);
    };
  }, [props.muted, props.className, mediaSrc]);

  return (
    <video
      src={mediaSrc}
      ref={videoRef}
      poster={white}
      preload="auto"
      playsInline
      {...props}
    />
  );
};
